import { useEffect, useState } from "react"
import { router } from "@inertiajs/react"
import { Check, ChevronLeft, ChevronRight, LoaderCircle, Search, UserMinus, UserPlus } from "lucide-react"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Switch } from "@/components/ui/switch"
import { Avatar } from "@/components/tuw/Avatar"
import StatusBadge from "@/components/tuw/StatusBadge"
import { csrfToken } from "@/lib/orderActions"
import { cn } from "@/lib/utils"

const postOpts = (onDone) => ({
  headers: { "X-CSRF-Token": csrfToken() },
  preserveScroll: true,
  onFinish: onDone,
})

// Create a new team. POSTs /team; the server seeds #general and redirects to the
// new team, so no client state to reconcile.
export function CreateTeamModal({ open, onOpenChange }) {
  const [name, setName] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setName("")
  }, [open])

  function submit(event) {
    event.preventDefault()
    if (!name.trim() || submitting) return
    setSubmitting(true)
    router.post("/team", { name }, postOpts(() => {
      setSubmitting(false)
      onOpenChange(false)
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">Create a team</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-name">Team name</Label>
            <Input
              id="team-name"
              placeholder="e.g. Fiction Crew"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
          <p className="text-[12px] leading-[1.5] text-ink-3">
            You&apos;ll get a #general channel to start. Invite freelancers you&apos;ve worked with and share
            projects once the team exists.
          </p>
          <DialogFooter className="gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? "Creating…" : "Create team"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Rename the active team. PATCHes /team/:id; the server redirects back with the
// new name, so no client state to reconcile.
export function RenameTeamModal({ open, onOpenChange, team }) {
  const [name, setName] = useState(team?.name ?? "")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setName(team?.name ?? "")
  }, [open, team?.name])

  function submit(event) {
    event.preventDefault()
    if (!name.trim() || !team?.id || submitting) return
    setSubmitting(true)
    router.patch(`/team/${team.id}`, { name }, postOpts(() => {
      setSubmitting(false)
      onOpenChange(false)
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">Rename team</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-team-name">Team name</Label>
            <Input
              id="rename-team-name"
              placeholder="e.g. Fiction Crew"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Create a named channel. POSTs /team/:id/channels; the server seeds the channel
// (order_id nil — the renameable half of the channel/thread invariant) and
// redirects to it, so no client state to reconcile.
export function CreateChannelModal({ open, onOpenChange, team }) {
  const [name, setName] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setName("")
  }, [open])

  function submit(event) {
    event.preventDefault()
    if (!name.trim() || !team?.id || submitting) return
    setSubmitting(true)
    router.post(`/team/${team.id}/channels`, { name }, postOpts(() => {
      setSubmitting(false)
      onOpenChange(false)
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">Create a channel</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="channel-name">Channel name</Label>
            <Input
              id="channel-name"
              placeholder="e.g. launch-plan"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? "Creating…" : "Create channel"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Rename a named channel. PATCHes /team/:id/channels/:conversation_id; the server
// resolves it STRICTLY as a named channel (order threads are never renameable)
// and redirects back with the new name.
export function RenameChannelModal({ open, onOpenChange, team, channel }) {
  const [name, setName] = useState(channel?.name ?? "")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) setName(channel?.name ?? "")
  }, [open, channel?.name])

  function submit(event) {
    event.preventDefault()
    if (!name.trim() || !team?.id || !channel?.id || submitting) return
    setSubmitting(true)
    router.patch(`/team/${team.id}/channels/${channel.id}`, { name }, postOpts(() => {
      setSubmitting(false)
      onOpenChange(false)
    }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">Rename channel</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="rename-channel-name">Channel name</Label>
            <Input
              id="rename-channel-name"
              placeholder="e.g. launch-plan"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
          <DialogFooter className="gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Search the complete eligible roster one server page at a time. Selection is
// retained across searches/pages and submitted as one atomic invitation set.
// Current members and pending invitees are removed by the server.
export function InviteModal({ open, onOpenChange, team }) {
  const [candidates, setCandidates] = useState([])
  const [query, setQuery] = useState("")
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(0)
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [selected, setSelected] = useState(() => new Map())
  const [note, setNote] = useState("")
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      setSelected(new Map())
      setNote("")
      setQuery("")
      setPage(1)
    }
  }, [open])

  useEffect(() => {
    if (!open || !team?.id) return undefined

    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      setLoading(true)
      setLoadError("")
      const params = new URLSearchParams({ page: String(page) })
      if (query.trim()) params.set("q", query.trim())

      try {
        const response = await fetch(`/team/${team.id}/freelancers?${params}`, {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
          signal: controller.signal,
        })
        const data = await response.json().catch(() => ({}))
        if (!response.ok) throw new Error(data.error || "We couldn't load the freelancer roster.")

        setCandidates(data.items ?? [])
        setPages(data.pages ?? 0)
        setTotal(data.total ?? 0)
      } catch (error) {
        if (error.name !== "AbortError") {
          setCandidates([])
          setLoadError(error.message)
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }, query ? 250 : 0)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [open, team?.id, query, page])

  function submit(event) {
    event.preventDefault()
    if (selected.size === 0 || submitting) return
    setSubmitting(true)
    router.post(`/team/${team.id}/invites`, { user_ids: Array.from(selected.keys()), note }, postOpts(() => {
      setSubmitting(false)
      onOpenChange(false)
    }))
  }

  function toggle(freelancer) {
    setSelected((current) => {
      const next = new Map(current)
      if (next.has(freelancer.userId)) next.delete(freelancer.userId)
      else if (next.size < 20) next.set(freelancer.userId, freelancer)
      return next
    })
  }

  const selectedCount = selected.size

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">
            Invite to {team?.name}
          </DialogTitle>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            Search the vetted roster. They&apos;ll get an invite to accept.
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="flex flex-col gap-4">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-3" strokeWidth={2} />
            <Input
              aria-label="Search freelancers"
              placeholder="Search by name or role"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value)
                setPage(1)
              }}
              className="pl-9"
              autoFocus
            />
          </div>

          <div className="flex max-h-[280px] flex-col gap-1 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center gap-2 px-2.5 py-6 text-[12.5px] text-ink-3">
                <LoaderCircle className="size-4 animate-spin" />
                Searching the roster…
              </div>
            )}
            {!loading && loadError && (
              <p role="alert" className="px-2.5 py-4 text-[12.5px] text-destructive">{loadError}</p>
            )}
            {!loading && !loadError && candidates.length === 0 && (
              <p className="px-2.5 py-4 text-[12.5px] text-ink-3">
                No available freelancers match that search.
              </p>
            )}
            {!loading && candidates.map((freelancer) => {
              const on = selected.has(freelancer.userId)
              const atLimit = selectedCount >= 20 && !on
              return (
                <button
                  key={freelancer.userId}
                  type="button"
                  onClick={() => toggle(freelancer)}
                  disabled={atLimit}
                  className={cn(
                    "flex items-center gap-[11px] rounded-[10px] border px-2.5 py-[9px] text-left transition-colors",
                    on ? "border-primary bg-[var(--red-soft)]" : "border-border hover:bg-accent",
                    atLimit && "cursor-not-allowed opacity-50"
                  )}
                >
                  <Avatar initials={freelancer.initials} name={freelancer.name} size={36} />
                  <div className="min-w-0 flex-1">
                    <h4 className="font-display text-[13.5px] font-semibold">{freelancer.name}</h4>
                    <div className="text-[11.5px] text-ink-3">
                      {[freelancer.role, ...(freelancer.specialties ?? [])].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  {on && <Check className="size-4 text-primary" strokeWidth={2.5} />}
                </button>
              )
            })}
          </div>

          {selectedCount > 0 && (
            <div className="rounded-[11px] border border-border bg-secondary/45 px-3 py-2.5">
              <div className="mb-1.5 text-[11px] font-bold tracking-[0.04em] text-ink-3 uppercase">
                Selected · {selectedCount}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(selected.values()).map((freelancer) => (
                  <button
                    key={freelancer.userId}
                    type="button"
                    onClick={() => toggle(freelancer)}
                    className="rounded-full bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground hover:text-primary"
                    aria-label={`Remove ${freelancer.name}`}
                  >
                    {freelancer.name} ×
                  </button>
                ))}
              </div>
            </div>
          )}

          {pages > 1 && (
            <div className="flex items-center justify-between text-[11.5px] font-semibold text-ink-3">
              <span>{total} available freelancers</span>
              <div className="flex items-center gap-2">
                <Button type="button" size="icon" className="size-8" variant="outline" aria-label="Previous roster page" disabled={page <= 1 || loading} onClick={() => setPage((value) => value - 1)}>
                  <ChevronLeft className="size-4" />
                </Button>
                <span>Page {page} of {pages}</span>
                <Button type="button" size="icon" className="size-8" variant="outline" aria-label="Next roster page" disabled={page >= pages || loading} onClick={() => setPage((value) => value + 1)}>
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-note">Add a note (optional)</Label>
            <Textarea
              id="invite-note"
              rows={2}
              placeholder="Let them know what you'd like their help with."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
            />
          </div>

          <DialogFooter className="gap-2.5">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || selectedCount === 0}>
              <UserPlus className="size-4" strokeWidth={2} />
              {submitting ? "Sending…" : selectedCount === 1 ? "Send invite" : `Send ${selectedCount} invites`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// Share standalone projects or whole/partial bundle groups. The UI groups by the
// durable order number, but the write remains a flat set of exact order ids so
// the controller's existing ownership and atomicity contract stays authoritative.
export function ShareOrderModal({ open, onOpenChange, team }) {
  const groups = team?.shareableOrderGroups ?? []
  const [submitting, setSubmitting] = useState(false)
  // Multi-select: the set of selected (unshared) order ids, an "Open a thread for
  // each" toggle (default on, per designRef team-workspace.html), and a dynamic
  // "Share N" action disabled at zero (AQ-005, decision 9).
  const [selected, setSelected] = useState(() => new Set())
  const [openThreads, setOpenThreads] = useState(true)

  // Reset selection whenever the modal opens.
  useEffect(() => {
    if (open) {
      setSelected(new Set())
      setOpenThreads(true)
    }
  }, [open])

  function toggle(orderId) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(orderId)) next.delete(orderId)
      else next.add(orderId)
      return next
    })
  }

  function toggleGroup(group) {
    const availableIds = group.items.filter((item) => !item.shared).map((item) => item.orderId)
    const allSelected = availableIds.length > 0 && availableIds.every((id) => selected.has(id))
    setSelected((prev) => {
      const next = new Set(prev)
      availableIds.forEach((id) => {
        if (allSelected) next.delete(id)
        else next.add(id)
      })
      return next
    })
  }

  function share() {
    if (submitting || selected.size === 0) return
    setSubmitting(true)
    router.post(
      `/team/${team.id}/orders`,
      { order_ids: Array.from(selected), open_thread_for_each: openThreads },
      postOpts(() => {
        setSubmitting(false)
        onOpenChange(false)
      })
    )
  }

  const count = selected.size

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle className="font-display text-[17px] font-bold tracking-[-0.2px]">
            Share projects with {team?.name}
          </DialogTitle>
          <p className="mt-0.5 text-[12.5px] text-ink-3">
            Select the projects to share. Shared projects are visible to every member of the team.
          </p>
        </DialogHeader>

        <div className="flex max-h-[340px] flex-col gap-2 overflow-y-auto">
          {groups.length === 0 && (
            <p className="px-2.5 py-4 text-[12.5px] text-ink-3">You don&apos;t have any projects to share yet.</p>
          )}
          {groups.map((group) => {
            const available = group.items.filter((item) => !item.shared)
            const selectedInGroup = available.filter((item) => selected.has(item.orderId)).length
            const groupChecked = selectedInGroup === available.length && available.length > 0
            const groupState = selectedInGroup > 0 && !groupChecked ? "indeterminate" : groupChecked

            return (
              <div key={group.key} className={cn("rounded-[12px] border border-border", group.bundle && "bg-secondary/25")}>
                {group.bundle && (
                  <div className="flex items-center gap-3 border-b border-border px-3.5 py-2.5">
                    <Checkbox
                      aria-label={`Select ${group.label}`}
                      checked={groupState}
                      onCheckedChange={() => toggleGroup(group)}
                      disabled={submitting || available.length === 0}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-display text-[13px] font-bold">{group.label}</div>
                      <div className="text-[10.5px] text-ink-3">
                        {group.items.length} services · choose all or any combination
                      </div>
                    </div>
                  </div>
                )}
                <div className={cn("flex flex-col gap-1", group.bundle ? "p-1.5" : "p-0")}>
                  {group.items.map((order) => (
                    <label
                      key={order.orderId}
                      className={cn(
                        "flex items-center gap-3 rounded-[10px] px-3.5 py-3 transition-colors",
                        !group.bundle && "rounded-[12px]",
                        order.shared ? "opacity-60" : "cursor-pointer hover:bg-accent"
                      )}
                    >
                      {!order.shared && (
                        <Checkbox
                          checked={selected.has(order.orderId)}
                          onCheckedChange={() => toggle(order.orderId)}
                          disabled={submitting}
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-display text-[13px] font-semibold">{order.title}</div>
                        <div className="text-[11px] text-ink-3">
                          {order.type}{order.itemNumber ? ` · Item ${order.itemNumber}` : ""}
                        </div>
                      </div>
                      {order.shared && (
                        <StatusBadge tier="done" label="Shared" className="px-2 py-0.5 text-[10px]" />
                      )}
                    </label>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <label className="flex items-center gap-2.5 rounded-[11px] border border-border px-3.5 py-2.5">
          <Switch checked={openThreads} onCheckedChange={setOpenThreads} disabled={submitting} />
          <span className="text-[12.5px] font-semibold">Open a thread for each</span>
        </label>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button type="button" onClick={share} disabled={submitting || count === 0}>
            {count === 0 ? "Share" : `Share ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Confirm before a freelancer is removed from the team (UPDA-87). The row's
// trash icon used to fire the DELETE straight away; deleting a team already
// asks first, and removal is the same kind of loss — the member drops out of
// every channel and shared project at once. One DS Dialog, no window.confirm.
//
// Controlled like DiscardDraftDialog: the parent holds the member being
// removed (null = closed) and performs the DELETE itself in onConfirm.
export function RemoveMemberModal({ member, onOpenChange, onConfirm }) {
  const open = !!member

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onOpenChange(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="grid size-9 shrink-0 place-items-center rounded-[11px] border border-border bg-secondary">
              <UserMinus className="size-4 text-destructive" strokeWidth={2} />
            </span>
            Remove {member?.name || "this freelancer"} from the team?
          </DialogTitle>
          <DialogDescription>
            They lose access to this team&rsquo;s channels and shared projects right away. You can invite them
            again later.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep member
          </Button>
          <Button variant="destructive" onClick={() => member && onConfirm(member)}>
            Remove from team
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
